import { changePercentDifference, getHumanReadableTime } from 'lib/numUtils';

const formatReports = (payload) => {
  let { reports } = payload;
  if (reports[0].data.rows === undefined) {
    return undefined;
  }
  reports = reports[0].data.rows;
  const formattedData = {};

  let sortedReports = reports
    .sort((item, nextItem) => {
      return parseInt(nextItem.metrics[0].values[0], 0) - parseInt(item.metrics[0].values[0], 0);
    })
    .splice(0, 6);

  sortedReports.forEach((obj) => {
    formattedData[obj.dimensions[0]] = parseInt(obj.metrics[0].values[0], 0);
  });

  delete formattedData['(not set)'];

  const leadLoopGraphFormat = [];
  Object.entries(formattedData).forEach(([key, value]) => {
    leadLoopGraphFormat.push({ label: key, currentData: value });
  });
  return leadLoopGraphFormat;
};

const formUserLocationReports = (payload) => {
  if (payload.reports[0].data.rows === undefined) {
    return undefined;
  }
  const data = payload.reports[0].data.rows.filter((row) => row.dimensions[0] !== '(not set)').splice(0, 10);
  const formattedData = [];

  data.forEach((item, index) => {
    const position = index;
    const region = item.dimensions[0];
    const city = item.dimensions[1];
    const lat = item.dimensions[2];
    const lng = item.dimensions[3];
    const newUsers = item.metrics[0].values[0];
    const data = {
      position,
      region,
      city,
      lat,
      lng,
      newUsers,
    };

    formattedData.push(data);
  });

  return formattedData.reverse();
};

const formTopLandingReports = (payload) => {
  if (payload.reports[0].data.rows === undefined) {
    return undefined;
  }
  let data = payload.reports[0].data.rows.splice(0, 10);
  const formattedArray = [];

  data.forEach((item) => {
    const thisItem = item.metrics[0].values[0];
    const lastItem = item.metrics[1].values[0];
    const obj = {
      page: item.dimensions[0],
      thisMonth: thisItem,
      lastMonth: lastItem,
      change: String(thisItem - lastItem),
      percentChange: changePercentDifference(thisItem, lastItem),
    };
    formattedArray.push(obj);
  });

  return formattedArray.filter((item) => item.page !== '(not set)');
};

const formatAnalyticsEvents = (payload) => {
  if (payload.reports[0].data.rows === undefined) {
    return undefined;
  }
  let data = payload.reports[0].data.rows.splice(0, 10);
  const formattedArray = [];
  data.forEach((item) => {
    let total = item.metrics[0].values[0];
    let dimensions = item.dimensions;
    const obj = {
      total,
      eventCategory: dimensions[0],
      eventAction: dimensions[1],
      eventLabel: dimensions[2],
    };
    formattedArray.push(obj);
  });
  return formattedArray;
};

const formatReportsSiteMetrics = (payload) => {
  let { reports } = payload;
  if (reports[0].data.rows === undefined) {
    return undefined;
  }
  let metrics = reports[0].data.rows[0].metrics;
  const thisMonthMetrics = metrics[0].values;
  const lastMonthMetrics = metrics[1].values;
  const formattedData = {};

  const displayNewUsers = thisMonthMetrics[4] > 1000 ? (thisMonthMetrics[4] / 1000).toFixed(1) + 'k' : Math.round(thisMonthMetrics[4]);
  formattedData['New Users'] = {
    title: 'New Users',
    value: displayNewUsers,
    change: changePercentDifference(thisMonthMetrics[4], lastMonthMetrics[4]),
    positiveChange: parseInt(thisMonthMetrics[4]) > parseInt(lastMonthMetrics[4]),
  };

  formattedData['Bounce Rate'] = {
    title: 'Bounce Rate',
    value: `${Math.round(thisMonthMetrics[3])}%`,
    change: Math.round(thisMonthMetrics[3]) - Math.round(lastMonthMetrics[3]) + '%',
    positiveChange: thisMonthMetrics[3] > lastMonthMetrics[3],
  };

  const displaySessions = thisMonthMetrics[5] > 1000 ? (thisMonthMetrics[5] / 1000).toFixed(1) + 'k' : Math.round(thisMonthMetrics[5]);
  formattedData['Sessions'] = {
    title: 'Sessions',
    value: displaySessions,
    change: changePercentDifference(thisMonthMetrics[5], lastMonthMetrics[5]),
    positiveChange: parseInt(thisMonthMetrics[5]) > parseInt(lastMonthMetrics[5]),
  };

  formattedData['Pages/Session'] = {
    title: 'Pages/Session',
    value: Math.round(thisMonthMetrics[2]),
    change: changePercentDifference(thisMonthMetrics[2], lastMonthMetrics[2]),
    positiveChange: parseInt(thisMonthMetrics[2]) > parseInt(lastMonthMetrics[2]),
  };

  formattedData['Avg. Session Duration'] = {
    title: 'Avg. Session Duration',
    value: getHumanReadableTime(thisMonthMetrics[0]),
    change: changePercentDifference(thisMonthMetrics[0], lastMonthMetrics[0]),
    positiveChange: parseInt(thisMonthMetrics[0]) > parseInt(lastMonthMetrics[0]),
  };

  const displayPageViews = thisMonthMetrics[1] > 1000 ? (thisMonthMetrics[1] / 1000).toFixed(1) + 'k' : Math.round(thisMonthMetrics[1]);
  formattedData['Page Views'] = {
    title: 'Page Views',
    value: displayPageViews,
    change: changePercentDifference(thisMonthMetrics[1], lastMonthMetrics[1]),
    positiveChange: parseInt(thisMonthMetrics[1]) > parseInt(lastMonthMetrics[1]),
  };

  const leadLoopGraphFormat = [];
  Object.entries(formattedData).forEach(([key, value]) => {
    leadLoopGraphFormat.push({ ...value });
  });

  return leadLoopGraphFormat;
};

export const crm_analytics_middleware =
  ({ dispatch }) =>
  (next) =>
  (action) => {
    if (action.type === 'CRM_FETCH_ANALYTICS_SOCIAL_SOURCES_SUCCESS') {
      action.payload = formatReports(action.payload);
    }
    if (action.type === 'CRM_FETCH_ANALYTICS_USER_LOCATIONS_SUCCESS') {
      action.payload = formUserLocationReports(action.payload);
    }
    if (action.type === 'CRM_FETCH_ANALYTICS_PAGE_VIEWS_SUCCESS') {
      action.payload = formTopLandingReports(action.payload);
    }
    if (action.type === 'CRM_FETCH_ANALYTICS_TOP_LANDING_SUCCESS') {
      action.payload = formTopLandingReports(action.payload);
    }
    if (action.type === 'CRM_FETCH_ANALYTICS_EVENTS_SUCCESS') {
      action.payload = formatAnalyticsEvents(action.payload);
    }
    if (action.type === 'CRM_FETCH_ANALYTICS_METRICS_SUCCESS') {
      const reportingCardFormat = formatReportsSiteMetrics(action.payload).map((item) => {
        return {
          label: item.title,
          value: item.value,
          description: '',
          isAnIncrease: item.positiveChange,
          percentage: item.change,
          percentage_description: `${item.title} are ${item.positiveChange === true ? 'up' : 'down'}`,
        };
      });

      action.payload = reportingCardFormat;
    }
    if (action.type === 'CRM_FETCH_ANALYTICS_DEVICES_SUCCESS') {
      const circlePackerFormat = formatReports(action.payload).map((item) => {
        return { label: item.label, value: item.currentData };
      });
      action.payload = circlePackerFormat;
    }
    next(action);
  };
